import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  LinearProgress, Chip, Stack, Button, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { useI18n } from '../../i18n/i18n';
import { getIncidents } from './incidentsService';
import { incidentPredictor } from './incidentsAIAnalytics';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import ScheduleIcon from '@mui/icons-material/Schedule';

export default function IncidentsAIDashboard() {
  const { t } = useI18n();
  const [predictions, setPredictions] = useState([]);
  const [modelInfo, setModelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState(false);

  useEffect(() => {
    loadAIData();
  }, []);

  const loadAIData = async () => {
    try {
      setLoading(true);
      const incidents = await getIncidents();
      
      if (incidents.length >= 10) {
        setTraining(true);
        const trainingResult = await incidentPredictor.trainModel(incidents);
        setModelInfo(trainingResult);
        
        const nextPredictions = incidentPredictor.predictNextIncidents(7);
        setPredictions(nextPredictions);
      }
    } catch (error) {
      console.error('Error loading AI data:', error);
    } finally {
      setLoading(false);
      setTraining(false);
    }
  };

  const getRiskColor = (count) => {
    if (count >= 4) return 'error';
    if (count >= 3) return 'warning';
    return 'success';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>{t('common.loading')}</Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">
            AI Аналітика Інцидентів
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Прогнози та рекомендації на основі машинного навчання
          </Typography>
        </Box>
      </Stack>

      {training && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Модель AI навчається на історичних даних...
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Інформація про модель */}
        {modelInfo && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🤖 Стан моделі ML
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Точність:</Typography>
                    <Chip 
                      label={`${Math.round(modelInfo.accuracy * 100)}%`}
                      color={modelInfo.accuracy > 0.7 ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Дані для тренування:</Typography>
                    <Typography fontWeight="bold">{modelInfo.trainingSize} записів</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>Визначені патерни:</Typography>
                    <Typography fontWeight="bold">{modelInfo.features}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Статистика прогнозів */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📈 Статистика прогнозів
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Прогнозовані інциденти (7 днів):</Typography>
                  <Typography fontWeight="bold">
                    {predictions.reduce((sum, day) => sum + day.expectedCount, 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Дні високого ризику:</Typography>
                  <Typography fontWeight="bold" color="warning.main">
                    {predictions.filter(p => p.expectedCount >= 3).length}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Рекомендації:</Typography>
                  <Typography fontWeight="bold">
                    {predictions.reduce((sum, day) => sum + day.recommendedActions.length, 0)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Прогнози на 7 днів */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                📅 Прогнози на наступні 7 днів
              </Typography>
              <Button variant="outlined" onClick={loadAIData}>
                Оновити прогнози
              </Button>
            </Stack>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Дата</TableCell>
                    <TableCell>Очікувані інциденти</TableCell>
                    <TableCell>Ризик</TableCell>
                    <TableCell>Типи високого ризику</TableCell>
                    <TableCell>Рекомендації</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {predictions.map((prediction, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {new Date(prediction.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" color="primary">
                          {prediction.expectedCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={prediction.expectedCount >= 4 ? 'Високий' : 
                                prediction.expectedCount >= 3 ? 'Середній' : 'Низький'}
                          color={getRiskColor(prediction.expectedCount)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {prediction.highRiskTypes.map((risk, idx) => (
                            <Chip
                              key={idx}
                              label={`${risk.type} (${Math.round(risk.confidence * 100)}%)`}
                              color={getConfidenceColor(risk.confidence)}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          {prediction.recommendedActions.map((action, idx) => (
                            <Typography key={idx} variant="body2" color="text.secondary">
                              • {action.message}
                            </Typography>
                          ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Рекомендації AI */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              💡 Рекомендації AI
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <WarningIcon color="warning" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Профілактика
                      </Typography>
                    </Stack>
                    <Typography variant="body2">
                      Перевірте обладнання в понеділок рано вранці для запобігання технічним інцидентам
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <ScheduleIcon color="info" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Оптимізація часу
                      </Typography>
                    </Stack>
                    <Typography variant="body2">
                      Заплановані обслуговування на ранкові години для мінімізації впливу на виробництво
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <TrendingUpIcon color="success" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Покращення процесів
                      </Typography>
                    </Stack>
                    <Typography variant="body2">
                      Впровадження додаткових перевірок для клієнтів з високою частотою інцидентів
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}